export class HttpError extends Error {
    readonly status: number;
    readonly payload: unknown;

    constructor(message: string, { status, payload }: { status: number; payload: unknown }) {
        super(message);
        this.name = "HttpError";
        this.status = status;
        this.payload = payload;
    }
}

export type HttpClientOptions = {
    fetcher?: typeof fetch;
    baseUrl?: string;
};

export type JsonRequestOptions = Omit<RequestInit, "body"> & {
    body?: unknown;
};

function createUrl(baseUrl: string, path: string): string {
    if (/^https?:\/\//.test(path))
        return path;

    return `${baseUrl}${path}`;
}

export class HttpClient {
    readonly fetcher: typeof fetch;
    readonly baseUrl: string;

    constructor({ fetcher = (...args) => window.fetch(...args), baseUrl = "" }: HttpClientOptions = {}) {
        this.fetcher = fetcher;
        this.baseUrl = baseUrl;
    }

    async json<TResponse>(path: string, options: JsonRequestOptions = {}): Promise<TResponse> {
        const headers = new Headers(options.headers);
        let body = options.body as BodyInit | null | undefined;

        if (options.body !== undefined && !(options.body instanceof FormData)) {
            headers.set("content-type", headers.get("content-type") ?? "application/json");
            body = JSON.stringify(options.body);
        }

        const response = await this.fetcher(createUrl(this.baseUrl, path), {
            ...options,
            headers,
            body,
            credentials: options.credentials ?? "same-origin",
        });
        const payload = await response.json().catch(() => null) as TResponse;

        if (!response.ok)
            throw new HttpError(response.statusText || "HTTP request failed", {
                status: response.status,
                payload,
            });

        return payload;
    }
}

export const DefaultHttpClient = new HttpClient();
